import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";

export const BUSINESS_METRIC_DEFINITIONS = [
  {
    id: "ORDER_RECEIVABLE",
    label: "订单应收",
    explanation: "本营业日订单应当收取的金额，不代表已经到账。",
    sourceLabel: "订单账本",
    basis: "RECEIVABLE",
    unit: "CNY_FEN",
    drillTarget: "ORDERS",
  },
  {
    id: "CONFIRMED_RECEIPTS",
    label: "已确认实收",
    explanation: "本营业日已经确认收到的款项，不包含待渠道核对金额。",
    sourceLabel: "付款流水",
    basis: "RECEIVED",
    unit: "CNY_FEN",
    drillTarget: "RECONCILIATION",
  },
  {
    id: "STORED_VALUE_PRINCIPAL",
    label: "储值本金变动",
    explanation: "本营业日会员储值本金的增加或减少，不能与营业实收相抵。",
    sourceLabel: "会员资金账本",
    basis: "STORED_VALUE_PRINCIPAL",
    unit: "CNY_FEN",
    drillTarget: "MEMBER_FUNDS",
  },
  {
    id: "BONUS_VALUE",
    label: "赠送金变动",
    explanation: "本营业日会员赠送金的增加或减少，不属于门店实际收款。",
    sourceLabel: "会员资金账本",
    basis: "BONUS_VALUE",
    unit: "CNY_FEN",
    drillTarget: "MEMBER_FUNDS",
  },
  {
    id: "REFUNDS",
    label: "退款",
    explanation: "本营业日已经记录的退款金额，单独展示，不从实收中隐藏抵减。",
    sourceLabel: "退款流水",
    basis: "REFUND",
    unit: "CNY_FEN",
    drillTarget: "RECONCILIATION",
  },
  {
    id: "ON_ACCOUNT_RECEIVABLE",
    label: "挂账应收",
    explanation: "本营业日已经形成但尚未收回的客户或会员欠款。",
    sourceLabel: "应收账本",
    basis: "ON_ACCOUNT",
    unit: "CNY_FEN",
    drillTarget: "RECEIVABLES",
  },
  {
    id: "PRODUCT_COST",
    label: "商品成本",
    explanation: "本营业日已售商品按成交时成本快照汇总；缺少快照时只显示已知部分。",
    sourceLabel: "订单商品与库存价值账本",
    basis: "COST",
    unit: "CNY_FEN",
    drillTarget: "ORDERS",
  },
  {
    id: "COMMISSION_PAYABLE",
    label: "提成应付",
    explanation: "本营业日服务记录形成的预计提成，不代表已经发放工资。",
    sourceLabel: "助教服务事实",
    basis: "PAYABLE",
    unit: "CNY_FEN",
    drillTarget: "COACH_SERVICES",
  },
  {
    id: "LOW_STOCK_PRODUCTS",
    label: "库存预警",
    explanation: "当前低库存和缺货商品数量；历史日期没有库存快照时不显示为零。",
    sourceLabel: "当前库存余额",
    basis: "COUNT",
    unit: "COUNT",
    drillTarget: "INVENTORY",
  },
  {
    id: "CASH_DIFFERENCE",
    label: "现金差异",
    explanation: "已完成交班的实盘现金与账面应有现金之差；未清点时不可用。",
    sourceLabel: "现金交班事实",
    basis: "CASH_DIFFERENCE",
    unit: "CNY_FEN",
    drillTarget: "CASH_SHIFTS",
  },
] as const;

export type BusinessMetricDefinition = (typeof BUSINESS_METRIC_DEFINITIONS)[number];
export type BusinessMetricId = BusinessMetricDefinition["id"];
export type BusinessMetricAvailability = "AVAILABLE" | "PARTIAL" | "NOT_AVAILABLE";

const BusinessMetricIdSchema = Type.Union(
  BUSINESS_METRIC_DEFINITIONS.map((definition) => Type.Literal(definition.id)),
);
const BusinessMetricAvailabilitySchema = Type.Union([
  Type.Literal("AVAILABLE"),
  Type.Literal("PARTIAL"),
  Type.Literal("NOT_AVAILABLE"),
]);

export const BusinessMetricReadModelSchema = Type.Object(
  {
    id: BusinessMetricIdSchema,
    label: Type.String({ minLength: 1, maxLength: 40 }),
    explanation: Type.String({ minLength: 1, maxLength: 160 }),
    sourceLabel: Type.String({ minLength: 1, maxLength: 60 }),
    basis: Type.Union([
      Type.Literal("RECEIVABLE"),
      Type.Literal("RECEIVED"),
      Type.Literal("STORED_VALUE_PRINCIPAL"),
      Type.Literal("BONUS_VALUE"),
      Type.Literal("REFUND"),
      Type.Literal("ON_ACCOUNT"),
      Type.Literal("COST"),
      Type.Literal("PAYABLE"),
      Type.Literal("COUNT"),
      Type.Literal("CASH_DIFFERENCE"),
    ]),
    unit: Type.Union([Type.Literal("CNY_FEN"), Type.Literal("COUNT")]),
    availability: BusinessMetricAvailabilitySchema,
    value: Type.Union([
      Type.Integer({ minimum: -Number.MAX_SAFE_INTEGER, maximum: Number.MAX_SAFE_INTEGER }),
      Type.Null(),
    ]),
    factCount: Type.Union([
      Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
      Type.Null(),
    ]),
    reason: Type.Union([Type.String({ minLength: 1, maxLength: 160 }), Type.Null()]),
    drillTarget: Type.Union([
      Type.Literal("ORDERS"),
      Type.Literal("RECONCILIATION"),
      Type.Literal("MEMBER_FUNDS"),
      Type.Literal("RECEIVABLES"),
      Type.Literal("COACH_SERVICES"),
      Type.Literal("INVENTORY"),
      Type.Literal("CASH_SHIFTS"),
    ]),
  },
  { additionalProperties: false },
);
export type BusinessMetricReadModel = Static<typeof BusinessMetricReadModelSchema>;

export const BusinessMetricDictionaryReadModelSchema = Type.Object(
  {
    tenantId: Type.String({ minLength: 1, maxLength: 128 }),
    storeId: Type.String({ minLength: 1, maxLength: 128 }),
    businessDate: Type.String({ pattern: "^\\d{4}-\\d{2}-\\d{2}$" }),
    timeZone: Type.String({ minLength: 1, maxLength: 80 }),
    businessDayCutover: Type.String({ pattern: "^(?:[01]\\d|2[0-3]):[0-5]\\d$" }),
    generatedAt: Type.String({ minLength: 1, maxLength: 64 }),
    coverage: Type.Union([
      Type.Literal("CURRENT_BUSINESS_DAY"),
      Type.Literal("RECORDED_FACTS"),
      Type.Literal("MISSING_HISTORY"),
    ]),
    metrics: Type.Array(BusinessMetricReadModelSchema, {
      minItems: BUSINESS_METRIC_DEFINITIONS.length,
      maxItems: BUSINESS_METRIC_DEFINITIONS.length,
    }),
  },
  { additionalProperties: false },
);
export type BusinessMetricDictionaryReadModel = Static<
  typeof BusinessMetricDictionaryReadModelSchema
>;

function isCanonicalDate(value: string): boolean {
  const milliseconds = Date.parse(`${value}T00:00:00.000Z`);
  return (
    Number.isFinite(milliseconds) && new Date(milliseconds).toISOString().slice(0, 10) === value
  );
}

function isCanonicalTimestamp(value: string): boolean {
  const milliseconds = Date.parse(value);
  return Number.isFinite(milliseconds) && new Date(milliseconds).toISOString() === value;
}

export function isBusinessMetricDictionaryReadModel(
  value: unknown,
  expectedScope?: { tenantId: string; storeId: string; businessDate: string },
): value is BusinessMetricDictionaryReadModel {
  if (!Value.Check(BusinessMetricDictionaryReadModelSchema, value)) return false;
  if (
    !isCanonicalDate(value.businessDate) ||
    !isCanonicalTimestamp(value.generatedAt) ||
    (expectedScope !== undefined &&
      (value.tenantId !== expectedScope.tenantId ||
        value.storeId !== expectedScope.storeId ||
        value.businessDate !== expectedScope.businessDate))
  ) {
    return false;
  }
  try {
    new Intl.DateTimeFormat("zh-CN", { timeZone: value.timeZone }).format(new Date(0));
  } catch {
    return false;
  }
  if (value.metrics.length !== BUSINESS_METRIC_DEFINITIONS.length) return false;
  for (const [index, metric] of value.metrics.entries()) {
    const definition = BUSINESS_METRIC_DEFINITIONS[index];
    if (
      definition === undefined ||
      metric.id !== definition.id ||
      metric.label !== definition.label ||
      metric.explanation !== definition.explanation ||
      metric.sourceLabel !== definition.sourceLabel ||
      metric.basis !== definition.basis ||
      metric.unit !== definition.unit ||
      metric.drillTarget !== definition.drillTarget ||
      (metric.availability === "NOT_AVAILABLE") !== (metric.value === null) ||
      (metric.availability === "NOT_AVAILABLE") !== (metric.factCount === null) ||
      (metric.availability === "AVAILABLE" && metric.reason !== null) ||
      (metric.availability !== "AVAILABLE" && metric.reason === null) ||
      (!["STORED_VALUE_PRINCIPAL", "BONUS_VALUE", "CASH_DIFFERENCE"].includes(metric.id) &&
        metric.value !== null &&
        metric.value < 0)
    ) {
      return false;
    }
  }
  return value.coverage !== "MISSING_HISTORY"
    ? true
    : value.metrics.every((metric) => metric.availability === "NOT_AVAILABLE");
}
